/**
 * @fileoverview One reporting point for the browser's direct Supabase traffic.
 *
 * Our own API has a choke point (`authenticatedRequest` → `ApiTransportError`)
 * that reports every failed request once. Supabase calls had none: ~40 call
 * sites query PostgREST straight from the browser, and each one decides for
 * itself whether a failure reaches Sentry. On 2026-10-15 the projects page
 * started sending a 25k-character `in.(…)` filter for an account with 722
 * projects; the Supabase gateway answered a bodyless 400 to every load, the
 * page caught it, logged it to the console and showed "Trouble connecting",
 * and Sentry recorded nothing for 15 hours.
 *
 * This wraps the `fetch` the Supabase client uses, so a failed request is
 * reported whatever the calling code does with the error. The response is
 * handed back untouched; the report runs detached and can never delay or
 * fail the request it describes.
 */
import { captureSentryError } from './sentry'

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>

export type SupabaseService = 'rest' | 'auth' | 'storage' | 'functions' | 'other'

export interface SupabaseRequestTarget {
  method: string
  service: SupabaseService
  /** Table, RPC, auth endpoint or function name. Never ids or values. */
  resource: string
  /** Length of the query string, the number that exposed the 2026-10-15 URL-limit bug. */
  queryLength: number
}

export type SupabaseRequestFailure =
  | (SupabaseRequestTarget & { kind: 'response'; status: number; code: string | null })
  | (SupabaseRequestTarget & { kind: 'network'; errorName: string })

/** One report per fingerprint per window, so a retry loop is one event, not hundreds. */
export const REPORT_WINDOW_MS = 10 * 60 * 1000

const SERVICE_BY_PREFIX: Record<string, SupabaseService> = {
  rest: 'rest',
  auth: 'auth',
  storage: 'storage',
  functions: 'functions',
}

/** Path segments become Sentry tags, so anything that is not a plain identifier is dropped. */
function identifierOr(segment: string | undefined, fallback: string): string {
  return segment && /^[A-Za-z0-9_-]{1,64}$/.test(segment) ? segment : fallback
}

export function describeSupabaseRequest(input: RequestInfo | URL, init?: RequestInit): SupabaseRequestTarget {
  const rawUrl = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
  const method = (init?.method ?? (typeof input === 'object' && 'method' in input ? input.method : 'GET')).toUpperCase()
  let url: URL
  try {
    url = new URL(rawUrl)
  } catch {
    return { method, service: 'other', resource: 'unknown', queryLength: 0 }
  }
  // /rest/v1/<table>, /rest/v1/rpc/<fn>, /auth/v1/<endpoint>, /storage/v1/object/…, /functions/v1/<name>
  const [prefix, , first, second] = url.pathname.split('/').filter(Boolean)
  const service = SERVICE_BY_PREFIX[prefix ?? ''] ?? 'other'
  const resource =
    service === 'rest' && first === 'rpc' ? `rpc:${identifierOr(second, 'unknown')}` : identifierOr(first, 'unknown')
  return { method, service, resource, queryLength: url.search.length }
}

interface PostgrestErrorBody {
  code?: unknown
  details?: unknown
}

async function readErrorBody(response: Response): Promise<PostgrestErrorBody | null> {
  try {
    const body: unknown = await response.clone().json()
    return body && typeof body === 'object' ? (body as PostgrestErrorBody) : null
  } catch {
    // The gateway's URL-length 400 has no body at all; a missing code is itself a signal.
    return null
  }
}

/**
 * Which failed responses are worth a report.
 *
 * - Any 5xx, from any service.
 * - PostgREST 4xx, except a `.single()` read that found no row (406 PGRST116,
 *   "contains 0 rows"): callers use that as an existence check. The same code
 *   with several rows is reported — it means a uniqueness assumption the
 *   database does not enforce has broken.
 * - Auth 4xx are not: an expired refresh token (400) or a session that ended
 *   (403) is a signed-out user, which the auth flow already handles.
 */
export function isReportableResponse(target: SupabaseRequestTarget, status: number, body: PostgrestErrorBody | null) {
  if (status >= 500) return true
  if (status < 400 || target.service !== 'rest') return false
  if (status === 406 && body?.code === 'PGRST116') {
    return !(typeof body.details === 'string' && /\b0 rows\b/.test(body.details))
  }
  return true
}

/**
 * A cancellation, not a failure. `abort(reason)` rejects with the caller's own
 * reason, which need not be an `AbortError`, so the signal is the authority.
 */
function isAbort(error: unknown, input: RequestInfo | URL, init?: RequestInit): boolean {
  if (init?.signal?.aborted) return true
  if (typeof Request !== 'undefined' && input instanceof Request && input.signal.aborted) return true
  return error instanceof Error && error.name === 'AbortError'
}

export function supabaseFailureFingerprint(failure: SupabaseRequestFailure): string[] {
  return failure.kind === 'network'
    ? ['supabase-request', 'network', failure.service]
    : [
        'supabase-request',
        failure.service,
        failure.method,
        failure.resource,
        String(failure.status),
        failure.code ?? 'no-code',
      ]
}

/**
 * Wrap a `fetch` so failed Supabase requests are reported. The returned
 * Response (or thrown error) is exactly what the base fetch produced.
 */
export function createMonitoredFetch(
  baseFetch: FetchLike,
  report: (failure: SupabaseRequestFailure) => void,
  clock: () => number = Date.now,
): FetchLike {
  const lastReportedAt = new Map<string, number>()

  const reportOnce = (failure: SupabaseRequestFailure) => {
    const key = supabaseFailureFingerprint(failure).join('|')
    const now = clock()
    const last = lastReportedAt.get(key)
    if (last !== undefined && now - last < REPORT_WINDOW_MS) return
    lastReportedAt.set(key, now)
    try {
      report(failure)
    } catch (error) {
      // Monitoring must never change what the caller sees: not the response,
      // not the error a network failure rethrows, not an unhandled rejection.
      console.warn('[supabase] failed to report a request failure:', error)
    }
  }

  return async (input, init) => {
    let response: Response
    try {
      response = await baseFetch(input, init)
    } catch (error) {
      if (!isAbort(error, input, init)) {
        const target = describeSupabaseRequest(input, init)
        reportOnce({ ...target, kind: 'network', errorName: error instanceof Error ? error.name : 'unknown' })
      }
      throw error
    }

    if (response.status >= 400) {
      const target = describeSupabaseRequest(input, init)
      void readErrorBody(response).then(body => {
        if (!isReportableResponse(target, response.status, body)) return
        const code = typeof body?.code === 'string' ? body.code : null
        reportOnce({ ...target, kind: 'response', status: response.status, code })
      })
    }
    return response
  }
}

/**
 * The Sentry issue title for a failure. Applied at the Sentry boundary
 * (`beforeSend` in instrumentation-client.ts), never as `Error.message`:
 * diagnostics stay on the instance (AGENTS.md → Error messages).
 */
export function supabaseFailureTitle(failure: SupabaseRequestFailure): string {
  return failure.kind === 'network'
    ? `Supabase ${failure.service} request failed before a response (${failure.errorName})`
    : `Supabase ${failure.method} ${failure.service}/${failure.resource} → ${failure.status}${failure.code ? ` ${failure.code}` : ''}`
}

/** Only ever reported, never thrown. The diagnostics live on `failure`. */
export class SupabaseRequestFailureReport extends Error {
  constructor(readonly failure: SupabaseRequestFailure) {
    super('Supabase request failed')
    this.name = 'SupabaseRequestFailure'
  }
}

export function isSupabaseRequestFailureReport(error: unknown): error is SupabaseRequestFailureReport {
  return error instanceof SupabaseRequestFailureReport
}

export function reportSupabaseFailure(failure: SupabaseRequestFailure): void {
  captureSentryError(new SupabaseRequestFailureReport(failure), {
    operation: 'supabase.request',
    level: failure.kind === 'network' ? 'warning' : 'error',
    fingerprint: supabaseFailureFingerprint(failure),
    tags: {
      supabase_service: failure.service,
      supabase_resource: failure.resource,
      supabase_method: failure.method,
      ...(failure.kind === 'response'
        ? { supabase_status: String(failure.status), supabase_code: failure.code ?? 'none' }
        : { supabase_failure: 'network' }),
    },
    extra: { queryLength: failure.queryLength },
  })
}

/** The `fetch` handed to the Supabase client. Resolves the global lazily so polyfills and test stubs apply. */
export const monitoredSupabaseFetch: FetchLike = createMonitoredFetch(
  (input, init) => fetch(input, init),
  reportSupabaseFailure,
)
